import { ArrowRight } from 'lucide-react';

import type { QualityScorecard as Scorecard } from '@/features/quality/types';

/**
 * The six-dimension customer-master quality scorecard — baseline vs. after a
 * governed remediation. The signature view for the GCC-bank story: composite
 * 87% → 93%, duplicates 19.4% → <0.1%, golden records built. Renders nothing
 * until a scorecard has been published (demo:seed-alwadi).
 */
export function QualityScorecard({ data }: { data: Scorecard }): JSX.Element | null {
  if (!data.dimensions || data.dimensions.length === 0) {
    return null;
  }

  const dupAfter =
    data.duplicate_rate_after !== undefined && data.duplicate_rate_after < 0.1
      ? '<0.1%'
      : `${data.duplicate_rate_after}%`;

  const headline = [
    {
      label: 'Quality composite',
      before: data.composite_before !== undefined ? `${data.composite_before}%` : '—',
      after: data.composite_after !== undefined ? `${data.composite_after}%` : '—',
    },
    {
      label: 'Duplicate customers',
      before: data.duplicate_rate_before !== undefined ? `${data.duplicate_rate_before}%` : '—',
      after: dupAfter,
    },
    {
      label: 'Golden records',
      before: '0',
      after: data.golden_records !== undefined ? data.golden_records.toLocaleString() : '—',
    },
  ];

  return (
    <section className="rounded-xl border border-ink-200/70 bg-white shadow-soft dark:border-white/10 dark:bg-ink-900">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-ink-100 px-6 py-4 dark:border-white/5">
        <div>
          <h3 className="h-section">{data.subject ?? 'Customer master'} — quality scorecard</h3>
          <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
            Six standard dimensions, measured against the source data — baseline vs. after governed remediation.
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-brand-700 ring-1 ring-brand-200 dark:bg-brand-500/15 dark:text-brand-300 dark:ring-brand-500/20">
          Baseline → After
        </span>
      </header>

      <div className="p-6">
        {/* Headline before → after stats */}
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {headline.map((h) => (
            <div
              key={h.label}
              className="rounded-lg border border-ink-200/70 bg-ink-50/40 p-4 dark:border-white/10 dark:bg-white/[0.03]"
            >
              <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-500 dark:text-ink-400">
                {h.label}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-lg font-semibold text-ink-400 line-through decoration-ink-300 dark:text-ink-500">
                  {h.before}
                </span>
                <ArrowRight className="h-4 w-4 text-ink-400 dark:text-ink-500" />
                <span className="font-display text-2xl font-bold text-brand-600 dark:text-brand-300">{h.after}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Per-dimension bars */}
        <div className="space-y-4">
          {data.dimensions.map((d) => {
            const delta = Math.round((d.after - d.before) * 10) / 10;
            const improved = delta >= 0.5;
            return (
              <div key={d.name}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-sm font-medium text-ink-700 dark:text-ink-200">{d.name}</span>
                  <span className="font-mono text-xs text-ink-500 dark:text-ink-400">
                    {d.before}% <span className="text-ink-400 dark:text-ink-500">→</span>{' '}
                    <span className="font-semibold text-brand-600 dark:text-brand-300">{d.after}%</span>
                    {improved && (
                      <span className="ml-1.5 rounded bg-brand-50 px-1.5 py-0.5 text-[10px] font-semibold text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">
                        +{delta}
                      </span>
                    )}
                  </span>
                </div>
                <div className="relative mt-1.5 h-2.5 overflow-hidden rounded-full bg-ink-100 dark:bg-white/5">
                  {/* "after" fill */}
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${d.after}%`,
                      background: improved
                        ? 'linear-gradient(90deg, #10b98199, #10b981)'
                        : 'linear-gradient(90deg, #64748b80, #64748b)',
                    }}
                  />
                  {/* baseline marker */}
                  <span
                    className="absolute top-1/2 h-3 w-px -translate-y-1/2 bg-ink-400 dark:bg-white/40"
                    style={{ left: `${d.before}%` }}
                    title={`Baseline ${d.before}%`}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-5 border-t border-ink-100 pt-3 text-xs text-ink-400 dark:border-white/5 dark:text-ink-500">
          Completeness &amp; Validity stay flat by design — missing or malformed values are surfaced as governed
          source-remediation tasks, never fabricated. The vertical mark on each bar shows the baseline.
        </p>
      </div>
    </section>
  );
}
